import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from "../../tailwind/button";
import { Badge } from "../../tailwind/badge";
import { Input } from "../../tailwind/input";
import { Card } from "../../tailwind/card";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "../../tailwind/dialog";
import { Label } from "../../tailwind/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../tailwind/select";
import { FileText, Globe, Phone } from 'lucide-react';
import { setCategory, resetCategoryData } from '../../../store/slices/templatesSlice';
import ButtonsPreview from './ButtonsPreview';

import { getLanguageFriendlyName } from "../../../pages/whatsapp/constants/templateConstants";
import { waBtn, waToggle } from "../../../pages/whatsapp/utils/whatsappUiTokens";
import { cn } from "../../../lib/utils";
import { WaTableSkeleton } from "../../../pages/whatsapp/components/skeletons/WhatsAppPageSkeletons";

export const TemplateLibrary = () => {
  const dispatch = useDispatch();
  const [libraryFilter, setLibraryFilter] = useState("All");
  
  const { templates, loading } = useSelector((state) => state.templates.categoryData.library || { templates: [], loading: false });

  // Library Preview State
  const [libraryPreviewTemplate, setLibraryPreviewTemplate] = useState(null);
  const [isLibraryPreviewOpen, setIsLibraryPreviewOpen] = useState(false);
  
  // Variables State for Library Preview
  const [templateVariables, setTemplateVariables] = useState({});
  const [templateName, setTemplateName] = useState("");
  const [selectedLanguageCode, setSelectedLanguageCode] = useState("en_US");
  const [templateErrors, setTemplateErrors] = useState({});
  const [buttonInputs, setButtonInputs] = useState([]);

  useEffect(() => {
    dispatch({ type: 'LOAD_TEMPLATE_LIBRARY' });
  }, [dispatch]);

  const extractVariables = (text) => {
    if (!text) return [];
    const regex = /\{\{([^}]+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (!matches.includes(match[1])) {
        matches.push(match[1]);
      }
    }
    return matches;
  };

  // Extract button definitions from library template.
  // Meta's library API returns buttons at template.buttons (top-level),
  // while regular templates use template.components[].buttons.
  const extractButtons = (template) => {
    let buttons = [];

    // Check top-level buttons (library API format)
    if (Array.isArray(template?.buttons)) {
      buttons = template.buttons;
    }
    // Fallback: check inside components array (regular API format)
    else if (Array.isArray(template?.components)) {
      const buttonComponent = template.components.find(c => c.type === 'BUTTONS');
      if (buttonComponent?.buttons) {
        buttons = buttonComponent.buttons;
      }
    }

    return buttons.filter(b => b.type === 'URL' || b.type === 'PHONE_NUMBER');
  };

  const activeTemplate = templates?.find(t => 
    t.name === libraryPreviewTemplate?.name && 
    (t.language || '').toLowerCase() === selectedLanguageCode.toLowerCase()
  ) || libraryPreviewTemplate;

  useEffect(() => {
    if (!activeTemplate) return;
    
    // Extract variables for preview
    const vars = extractVariables(activeTemplate.body);
    const initialVars = {};
    vars.forEach(v => { initialVars[v] = ""; });
    setTemplateVariables(initialVars);

    // Initialize button inputs
    const buttons = extractButtons(activeTemplate);
    const initialButtonInputs = buttons.map(btn => ({
      type: btn.type,
      text: btn.text || '',
      // For URL buttons
      base_url: btn.url || '',
      url_suffix_example: '',
      // For PHONE_NUMBER buttons
      phone_number: '',
    }));
    setButtonInputs(initialButtonInputs);
  }, [activeTemplate]);

  const handleLibraryPreview = (template) => {
    setLibraryPreviewTemplate(template);
    setTemplateName("");
    setSelectedLanguageCode((template.language || "en").toLowerCase());
    setTemplateErrors({});
    setIsLibraryPreviewOpen(true);
  };


  const renderTemplateBody = (text) => {
    if (!text) return null;
    const parts = text.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, i) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        return <span key={i} className="text-[#0F6E56] font-medium">{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  const renderReplacedBody = () => {
    if (!activeTemplate?.body) return null;
    let text = activeTemplate.body;
    
    const parts = text.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, i) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        const varName = part.slice(2, -2);
        const val = templateVariables[varName];
        if (val) {
          return <span key={i} className="bg-[#0F6E56]/10 text-[#0F6E56] px-1 rounded-sm">{val}</span>;
        } else {
          return <span key={i} className="text-[#0F6E56] font-medium">{part}</span>;
        }
      }
      return <span key={i}>{part}</span>;
    });
  };

  const handleButtonInputChange = (index, field, value) => {
    setButtonInputs(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    // Clear error for this button field
    const errorKey = `button_${index}_${field}`;
    if (templateErrors[errorKey]) {
      setTemplateErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleLibrarySave = () => {
    const errors = {};
    if (!templateName.trim()) {
      errors.name = "Template name is required";
    } else if (!/^[a-z0-9_]+$/.test(templateName)) {
      errors.name = "Lowercase letters and underscores only";
    }

    // Validate button inputs
    buttonInputs.forEach((btn, idx) => {
      if (btn.type === 'URL') {
        if (!btn.base_url?.trim()) {
          errors[`button_${idx}_base_url`] = "Website URL is required";
        }
      } else if (btn.type === 'PHONE_NUMBER') {
        if (!btn.phone_number?.trim()) {
          errors[`button_${idx}_phone_number`] = "Phone number is required";
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setTemplateErrors(errors);
      return;
    }

    const targetCategory = (activeTemplate?.category || "UTILITY").toLowerCase();

    // Build library_template_button_inputs in Meta's required format
    const libraryTemplateButtonInputs = buttonInputs.map(btn => {
      if (btn.type === 'URL') {
        return {
          type: 'URL',
          url: {
            base_url: btn.base_url,
            url_suffix_example: btn.url_suffix_example || undefined,
          },
        };
      } else if (btn.type === 'PHONE_NUMBER') {
        return {
          type: 'PHONE_NUMBER',
          phone_number: btn.phone_number,
        };
      }
      return { type: btn.type };
    });
    
    // For library templates, Meta already has the body/header/footer/buttons.
    // We only need name, category, language, library reference, and button inputs.
    dispatch({
      type: 'CREATE_WHATSAPP_TEMPLATE',
      payload: {
        templateName,
        category: activeTemplate?.category || "UTILITY",
        language: selectedLanguageCode,
        libraryTemplateName: activeTemplate?.name,
        libraryTemplateButtonInputs: libraryTemplateButtonInputs.length > 0 ? libraryTemplateButtonInputs : undefined,
        mediaType: 'none',
      }
    });

    // Save cloned template structure locally to support previewing before backend sync completes
    try {
      localStorage.setItem(`template_library_cloned_${templateName}`, JSON.stringify(activeTemplate));
    } catch (e) {
      console.error('Failed to save local preview cache:', e);
    }

    setIsLibraryPreviewOpen(false);

    // Invalidate cache for the target category and switch to it
    if (['utility', 'marketing', 'authentication'].includes(targetCategory)) {
      dispatch(resetCategoryData(targetCategory));
      dispatch(setCategory(targetCategory));
    } else {
      dispatch(resetCategoryData('utility'));
      dispatch(setCategory('utility'));
    }
  };



  const getTemplateTag = (t) => {
    if (t.usecase) return t.usecase.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    if (t.topic) return t.topic.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    return t.category;
  };

  // Show unique templates by name in the gallery
  const uniqueTemplates = [];
  const seenTemplateNames = new Set();
  
  (templates || []).forEach(t => {
    if (!seenTemplateNames.has(t.name)) {
      seenTemplateNames.add(t.name);
      uniqueTemplates.push(t);
    }
  });

  uniqueTemplates.sort((a, b) => {
    const tagA = getTemplateTag(a) || '';
    const tagB = getTemplateTag(b) || '';
    const tagCompare = tagA.localeCompare(tagB);
    if (tagCompare !== 0) return tagCompare;
    return (a.name || '').localeCompare(b.name || '');
  });

  const availableTags = Array.from(new Set(uniqueTemplates.map(getTemplateTag).filter(Boolean)));
  availableTags.sort((a, b) => a.localeCompare(b));
  const filterTags = ["All", ...availableTags];

  const filteredLibraryTemplates = uniqueTemplates.filter(t => 
    libraryFilter === "All" || (getTemplateTag(t) === libraryFilter)
  );

  const allowedLanguageCodes = ['en', 'hi'];
  const availableTranslations = templates?.filter(t => t.name === libraryPreviewTemplate?.name) || [];
  const uniqueTranslations = [];
  const seenLangs = new Set();
  availableTranslations.forEach(t => {
    const lang = t.language || 'en';
    const normalizedLang = lang.toLowerCase().replace('-', '_');
    if (allowedLanguageCodes.includes(normalizedLang) && !seenLangs.has(normalizedLang)) {
      seenLangs.add(normalizedLang);
      uniqueTranslations.push(t);
    }
  });
  if (uniqueTranslations.length === 0 && libraryPreviewTemplate) {
    uniqueTranslations.push(libraryPreviewTemplate);
  }

  const previewButtons = buttonInputs.map(btn => ({
    type: btn.type === 'PHONE_NUMBER' ? 'CALL' : btn.type,
    text: btn.text,
    url: btn.base_url,
    phoneNumber: btn.phone_number
  }));

  return (
    <div className="mt-6">
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
        {filterTags.map(filter => (
          <button
            key={filter}
            onClick={() => setLibraryFilter(filter)}
            className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
              libraryFilter === filter 
                ? cn(waToggle.selected) 
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      {loading ? (
        <WaTableSkeleton rows={6} cols={3} />
      ) : filteredLibraryTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLibraryTemplates.map((template, idx) => (
            <Card 
              key={template.id || `template_${idx}`} 
              className="border-gray-200 hover:border-gray-300 transition-colors flex flex-col shadow-sm overflow-hidden"
            >
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-2">
                   <Badge variant="secondary" className="bg-gray-100 text-gray-600 font-normal rounded-md px-2 py-0.5">{getTemplateTag(template)}</Badge>
                </div>
                <h4 className="font-bold text-[13px] text-gray-900 mb-2">{template.name || `Template ${template.id || idx + 1}`}</h4>
                <p className="text-[12px] text-gray-500 line-clamp-3 mb-4 leading-relaxed flex-1">
                  {renderTemplateBody(template.body)}
                </p>
              </div>
              <div className="p-3 bg-gray-50 border-t border-gray-100">
                <Button 
                  onClick={() => handleLibraryPreview(template)}
                  className={cn("w-full h-9 text-sm font-medium", waBtn.primary)}
                >
                  Use this template
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="py-16 text-center border border-dashed border-gray-200 rounded-lg">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-900 font-medium">No templates in this category yet</p>
          <p className="text-sm text-gray-500 mt-1">Try a different filter or check back later</p>
        </div>
      )}

      {/* Library Preview Dialog */}
      <Dialog open={isLibraryPreviewOpen} onOpenChange={setIsLibraryPreviewOpen}>
        <DialogContent className="w-[95vw] md:w-full md:max-w-[800px] max-h-[90vh] md:max-h-[95vh] overflow-y-auto md:overflow-hidden p-0 [&>button]:top-5 [&>button]:right-5 [&>button]:z-50">
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
            <DialogTitle className="text-lg font-medium">Use template</DialogTitle>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left Column - Form */}
            <div className="p-6 border-b md:border-b-0 md:border-r border-gray-100 max-h-[50vh] md:max-h-[60vh] overflow-y-auto space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Fill your details</h3>
                <div className="space-y-1.5">
                  <Label htmlFor="template-name" className="text-xs text-gray-700">Template name <span className="text-red-500">*</span></Label>
                  <Input 
                    id="template-name"
                    placeholder="e.g. complaint_resolved"
                    value={templateName}
                    onChange={(e) => {
                      const processed = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                      setTemplateName(processed);
                      if (templateErrors.name) {
                        setTemplateErrors(prev => {
                          const newErrs = {...prev};
                          delete newErrs.name;
                          return newErrs;
                        });
                      }
                    }}
                    className={`h-9 ${templateErrors.name ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {templateErrors.name ? (
                    <p className="text-[11px] text-red-500">{templateErrors.name}</p>
                  ) : (
                    <p className="text-[11px] text-gray-500">Lowercase letters and underscores only (Meta requirement)</p>
                  )}
                </div>
              </div>

              {/* Button Inputs Section */}
              {buttonInputs.length > 0 && (
                <div>
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Configure buttons</h3>
                    <p className="text-[11px] text-gray-500">These buttons are part of the library template</p>
                  </div>
                  
                  <div className="space-y-4">
                    {buttonInputs.map((btn, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          {btn.type === 'URL' ? (
                            <Globe className="h-3.5 w-3.5 text-[#0F6E56]" />
                          ) : (
                            <Phone className="h-3.5 w-3.5 text-[#0F6E56]" />
                          )}
                          <span className="text-xs font-medium text-gray-700">
                            {btn.text || (btn.type === 'URL' ? 'Website Button' : 'Phone Button')}
                          </span>
                          <Badge variant="secondary" className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0">{btn.type}</Badge>
                        </div>
                        
                        {btn.type === 'URL' && (
                          <>
                            <div className="space-y-1">
                              <Label className="text-[11px] text-gray-600">Website URL <span className="text-red-500">*</span></Label>
                              <Input
                                placeholder="https://www.example.com/"
                                value={btn.base_url}
                                onChange={(e) => handleButtonInputChange(idx, 'base_url', e.target.value)}
                                className={`h-8 text-sm ${templateErrors[`button_${idx}_base_url`] ? 'border-red-500' : ''}`}
                              />
                              {templateErrors[`button_${idx}_base_url`] && (
                                <p className="text-[11px] text-red-500">{templateErrors[`button_${idx}_base_url`]}</p>
                              )}
                            </div>
                          </>
                        )}
                        
                        {btn.type === 'PHONE_NUMBER' && (
                          <div className="space-y-1">
                            <Label className="text-[11px] text-gray-600">Phone number <span className="text-red-500">*</span></Label>
                            <Input
                              placeholder="+91 9876543210"
                              value={btn.phone_number}
                              onChange={(e) => handleButtonInputChange(idx, 'phone_number', e.target.value)}
                              className={`h-8 text-sm ${templateErrors[`button_${idx}_phone_number`] ? 'border-red-500' : ''}`}
                            />
                            {templateErrors[`button_${idx}_phone_number`] && (
                              <p className="text-[11px] text-red-500">{templateErrors[`button_${idx}_phone_number`]}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Variable Preview Section (read-only for library templates) */}
              {activeTemplate && extractVariables(activeTemplate.body).length > 0 && (
                <div>
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Template variables</h3>
                    <p className="text-[11px] text-gray-500">These will be filled when sending the message</p>
                  </div>
                  <div className="space-y-2">
                    {extractVariables(activeTemplate.body).map(variable => (
                      <div key={variable} className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-1 rounded bg-[#0F6E56]/10 text-[#0F6E56] text-[11px] font-medium">
                          {`{{${variable}}}`}
                        </span>
                        <span className="text-[11px] text-gray-400">— filled at send time</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="language-select" className="text-xs text-gray-700 mb-1.5 block">Language</Label>
                <Select value={selectedLanguageCode} onValueChange={setSelectedLanguageCode}>
                  <SelectTrigger id="language-select" className="h-9">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueTranslations.map(t => {
                      const langCode = t.language || 'en';
                      return (
                        <SelectItem key={langCode} value={langCode}>
                          {getLanguageFriendlyName(langCode)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Right Column - Preview */}
            <div className="p-6 bg-gray-50/50 flex flex-col items-center">
              <div className="w-full max-w-[300px]">
                <Label className="text-[11px] text-gray-500 mb-3 block text-center font-semibold uppercase tracking-wider">MESSAGE PREVIEW</Label>
                <div className="bg-[#efeae2] p-4 rounded-xl min-h-[200px] flex items-start justify-center shadow-inner relative">
                   <div className="bg-white p-3 rounded-xl rounded-tl-none shadow-sm w-full relative">
                      <p className="text-[13px] text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {renderReplacedBody()}
                      </p>
                      <div className="text-[10px] text-gray-400 text-right mt-2 pr-1 pb-1">
                        11:42
                      </div>
                      <ButtonsPreview buttons={previewButtons} />
                   </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="p-4 px-6 border-t border-gray-100 bg-gray-50 flex justify-end w-full">
            <Button 
              className={cn("shadow-sm px-6", waBtn.primary)} 
              onClick={handleLibrarySave}
            >
              Save & Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
